import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight, ChevronDown, Building2, ListFilter } from "lucide-react";
import { NAV } from "@/constants/testIds";

const navLinks = [
  {
    label: "Properties", href: "/properties",
    dropdown: [
      { label: "All Properties", href: "/properties", desc: "Browse full inventory" },
      { label: "Featured Listings", href: "/listings", desc: "Sai World Empire, Waterfall & more" },
    ]
  },
  { label: "Developers", href: "/developers" },
  { label: "Why Navi Mumbai", href: "/why-navi-mumbai" },
  { label: "Insights", href: "/insights" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpenDropdown(null); }, [location]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass border-b border-white/5 py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" ref={dropdownRef}>
          {/* Logo */}
          <Link to="/" data-testid={NAV.logo} className="flex items-center">
            <img src="/sneha-logo.jpg" alt="Sneha Reality"
              className="h-10 w-auto object-contain"
              style={{ filter: "drop-shadow(0 0 8px rgba(212,175,55,0.25))" }} />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) =>
              l.dropdown ? (
                <div key={l.href} className="relative">
                  <button
                    onClick={() => setOpenDropdown(openDropdown === l.href ? null : l.href)}
                    className={`flex items-center gap-1.5 text-sm font-heading font-light tracking-wider uppercase transition-colors duration-300 ${
                      location.pathname.startsWith("/properties") || location.pathname === "/listings"
                        ? "text-[#D4AF37]" : "text-[#FAF8F3]/70 hover:text-[#D4AF37]"
                    }`}
                  >
                    {l.label}
                    <ChevronDown size={12} className={`transition-transform duration-200 ${openDropdown === l.href ? "rotate-180" : ""}`} />
                  </button>

                  <AnimatePresence>
                    {openDropdown === l.href && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-3 w-64 border border-[#D4AF37]/15 bg-[#080808] shadow-2xl overflow-hidden"
                        style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.8), 0 0 30px rgba(212,175,55,0.05)" }}
                      >
                        {l.dropdown.map((d) => (
                          <Link key={d.href} to={d.href}
                            onClick={() => setOpenDropdown(null)}
                            className="flex items-start gap-3 px-5 py-4 border-b border-white/5 last:border-b-0 hover:bg-[#D4AF37]/5 transition-colors group"
                          >
                            <div className="w-7 h-7 border border-[#D4AF37]/20 flex items-center justify-center shrink-0 mt-0.5 group-hover:border-[#D4AF37]/50 transition-colors">
                              {d.href === "/listings" ? <ListFilter size={12} className="text-[#D4AF37]" /> : <Building2 size={12} className="text-[#D4AF37]" />}
                            </div>
                            <div>
                              <p className="text-sm text-[#FAF8F3] font-heading font-light tracking-wide group-hover:text-[#D4AF37] transition-colors">{d.label}</p>
                              <p className="text-[10px] text-[#555] mt-0.5">{d.desc}</p>
                            </div>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link key={l.href} to={l.href}
                  className={`text-sm font-heading font-light tracking-wider uppercase transition-colors duration-300 ${
                    location.pathname === l.href ? "text-[#D4AF37]" : "text-[#FAF8F3]/70 hover:text-[#D4AF37]"
                  }`}
                >
                  {l.label}
                </Link>
              )
            )}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/consultation" data-testid={NAV.consultationCta} className="btn-gold text-xs py-3 px-6">
              Book Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button data-testid={NAV.mobileMenuBtn}
            className="lg:hidden p-2 text-[#FAF8F3]/80 hover:text-[#D4AF37] transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div data-testid={NAV.mobileMenu}
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 glass flex flex-col pt-24 px-8 pb-8 overflow-y-auto"
          >
            <div className="flex flex-col gap-4">
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                className="pb-6 border-b border-white/5 flex justify-center">
                <img src="/sneha-logo.jpg" alt="Sneha Reality" className="h-20 w-auto object-contain" />
              </motion.div>

              {navLinks.map((l, i) =>
                l.dropdown ? (
                  <motion.div key={l.href} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06 }}>
                    <p className="text-sm font-heading text-[#D4AF37] tracking-wider uppercase pb-3 border-b border-white/5 mb-2">{l.label}</p>
                    {l.dropdown.map((d) => (
                      <Link key={d.href} to={d.href}
                        className="flex items-center justify-between text-base font-heading font-light text-[#FAF8F3]/70 hover:text-[#D4AF37] transition-colors pl-3 border-b border-white/[0.04] pb-3 mb-2">
                        {d.label} <ChevronRight size={14} className="opacity-40" />
                      </Link>
                    ))}
                  </motion.div>
                ) : (
                  <motion.div key={l.href} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06 }}>
                    <Link to={l.href}
                      className="flex items-center justify-between text-lg font-heading font-light text-[#FAF8F3]/80 hover:text-[#D4AF37] transition-colors border-b border-white/5 pb-4">
                      {l.label} <ChevronRight size={16} className="opacity-40" />
                    </Link>
                  </motion.div>
                )
              )}

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="mt-4">
                <Link to="/consultation" className="btn-gold-solid btn-gold w-full justify-center">
                  Book Private Consultation
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
